import React, { useState } from 'react';

import { Icon, Text } from '@betterment/samba-react';
import { Box, Button, ButtonProps, Flex, Slot, getSlots, get as themeGet } from '@betterment/style-closet-react';
import styled from '@emotion/styled';
import { Disclosure, DisclosurePanel, DisclosureButton as _DisclosureButton } from '@reach/disclosure';

interface CardProps {
  collapsible?: boolean;
  collapsibleText?: React.ReactNode | string;
  children: React.ReactElement | React.ReactElement[];
  collapsed?: boolean;
  onChange?: (collapsed: boolean) => void;
}

interface CardSlotProps {
  name: 'header' | 'body' | 'footer';
}

interface CollapsibleCardProps {
  collapsibleText: React.ReactNode | string;
  header: React.ReactNode;
  body: React.ReactNode;
  footer?: React.ReactNode;
  collapsed: boolean;
  onChange?: (collapsed: boolean) => void;
}

type CardComponent = React.FC<React.PropsWithChildren<CardProps>> & {
  Slot: React.FC<React.PropsWithChildren<CardSlotProps>>;
};

const DisclosureButton = ({ type = 'button', variant = 'tertiary', size = 'default', ...props }: ButtonProps) => (
  <Button type={type} variant={variant} size={size} {...props} as={_DisclosureButton} />
);

const StyledDisclosureButton = styled(DisclosureButton)`
  background: ${themeGet('colors.bmtWhite')};
  width: 100%;

  &:hover,
  &:focus {
    text-decoration: none;
  }
`;

const CollapsibleCard: React.FC<React.PropsWithChildren<CollapsibleCardProps>> = ({
  collapsibleText,
  header,
  body,
  footer,
  collapsed,
  onChange,
}) => {
  const [isCollapsed, setCollapsed] = useState(collapsed);

  const handleChange = () => {
    setCollapsed(!isCollapsed);
    if (onChange) {
      onChange(!isCollapsed);
    }
  };

  return (
    <Box backgroundColor="bmtWhite">
      <Disclosure open={!isCollapsed} onChange={handleChange}>
        <StyledDisclosureButton>
          <Box pt={8} pb={6} borderBottom={!isCollapsed ? '1px solid' : null} borderColor="bmtGrey20">
            <Flex justifyContent="space-between" alignItems="center">
              <Text
                css={{
                  textAlign: 'left',
                }}
              >
                {header}
              </Text>
              {!isCollapsed && <Icon name="CaretDown" size="small" color="icon" mode="functional" label="Close" />}
              {isCollapsed && (
                <Flex>
                  <Text
                    css={{
                      marginRight: 'space.100',
                    }}
                  >
                    {collapsibleText}
                  </Text>
                  <Icon name="CaretUp" size="small" color="icon" mode="functional" label="Open" />
                </Flex>
              )}
            </Flex>
          </Box>
        </StyledDisclosureButton>
        <DisclosurePanel>
          <Box py={8} borderColor="bmtGrey20">
            {body}
          </Box>
          {footer && <Box py={8}>{footer}</Box>}
        </DisclosurePanel>
      </Disclosure>
    </Box>
  );
};

const Card: CardComponent = ({ collapsible = false, collapsibleText = '', collapsed = false, onChange, children }) => {
  const [header, body, footer] = getSlots(['header', 'body', 'footer'], children);
  const bodyBorderBottom = footer ? '1px solid' : '';

  if (collapsible) {
    return (
      <CollapsibleCard
        header={header}
        body={body}
        footer={footer}
        collapsibleText={collapsibleText}
        collapsed={collapsed}
        onChange={onChange}
      />
    );
  }
  return (
    <Box backgroundColor="bmtWhite">
      {header && (
        <Box py={8} borderBottom="1px solid" borderColor="bmtGrey20">
          {header}
        </Box>
      )}
      <Box py={8} borderBottom={bodyBorderBottom} borderColor="bmtGrey20">
        {body}
      </Box>
      {footer && <Box py={8}>{footer}</Box>}
    </Box>
  );
};

Card.Slot = Slot as React.FC<React.PropsWithChildren<CardSlotProps>>;

export default Card;
